import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { CheckboxLine, HpButton, HpCardHeader, HpField } from '@hp/ui';
import { queryClient } from '../../api/queryClient';
import { trpc } from '../../api/trpc';
import { isRunActive } from '../../lib/runs';
import { useSegmentationRuns } from './queries';
import { ErrorAlert } from '../../components/error-alert';
import { Spinner } from '../../components/ui/icons';
import { fmtDateTime, fmtInt, progressOf } from '../../lib/format';

/** Admin controls: start a run (LLM toggle + cap) and show the active run's progress. */
export function RunPanel({ canRun }: { canRun: boolean }) {
  const { t } = useTranslation(['segmentation', 'common']);
  const runs = useSegmentationRuns();
  const activeRun = runs.data?.find((r) => isRunActive(r.status));
  const lastRun = runs.data?.[0];

  const [llm, setLlm] = useState(true);
  const [llmCap, setLlmCap] = useState('500');

  const start = useMutation(
    trpc.segmentation.startRun.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries(trpc.segmentation.pathFilter()),
    }),
  );

  const progress = progressOf(activeRun?.stats?.progress);
  const llmProgress = progressOf(activeRun?.stats?.llmProgress);
  const scored = progress?.processed ?? 0;

  const statusLine = activeRun ? (
    <Spinner>
      {llmProgress
        ? t('run.aiPass', { processed: llmProgress.processed ?? 0, of: llmProgress.of ?? '?' })
        : t('run.scoring', { count: scored, n: fmtInt(scored) })}
    </Spinner>
  ) : lastRun ? (
    <span className="bo-muted bo-mono">
      {lastRun.finishedAt
        ? t('run.lastRunAt', {
            id: lastRun.id,
            status: t(`common:status.${lastRun.status}`, { defaultValue: lastRun.status }),
            at: fmtDateTime(lastRun.finishedAt),
          })
        : t('run.lastRun', {
            id: lastRun.id,
            status: t(`common:status.${lastRun.status}`, { defaultValue: lastRun.status }),
          })}
    </span>
  ) : (
    <span className="bo-muted">{t('run.noRuns')}</span>
  );

  return (
    <section className="hp-card">
      <HpCardHeader title={t('run.title')} subtitle={t('run.subtitle')} icon="target">
        {statusLine}
      </HpCardHeader>
      {canRun && (
        <div className="hp-card__body bo-sections">
          <div className="bo-inline-form">
            <div className="bo-inline-form__submit">
              <HpButton
                variant="primary"
                icon="play"
                busy={start.isPending}
                disabled={activeRun !== undefined}
                onClick={() => start.mutate({ llm, llmCap: llm ? Number(llmCap) || 0 : 0 })}
              >
                {activeRun ? t('run.inProgress') : t('run.start')}
              </HpButton>
            </div>
            <div className="bo-inline-form__submit">
              <CheckboxLine checked={llm} onChange={(e) => setLlm(e.target.checked)}>
                {t('run.llmToggle')}
              </CheckboxLine>
            </div>
            {llm && (
              <HpField label={t('run.llmCap')} className="bo-field--narrow">
                <input value={llmCap} onChange={(e) => setLlmCap(e.target.value)} inputMode="numeric" />
              </HpField>
            )}
          </div>
          {llm && !activeRun && (
            <p className="bo-hint">
              <Trans t={t} i18nKey="run.hint" components={{ em: <em /> }} />
            </p>
          )}
          {start.isError && <ErrorAlert error={start.error} />}
          {lastRun?.status === 'failed' && lastRun.error && <ErrorAlert error={new Error(lastRun.error)} />}
        </div>
      )}
      {!canRun && lastRun?.status === 'failed' && lastRun.error && (
        <ErrorAlert error={new Error(lastRun.error)} className="bo-alert--inset" />
      )}
    </section>
  );
}
